<template>
  <input
    ref="inputRef"
    type="text"
    inputmode="decimal"
    :value="formattedValue"
    style="font: inherit"
    class="
      no-hover-bg
      tw-w-full tw-border-none tw-bg-transparent tw-outline-none tw-text-right
      !tw-ring-0 !tw-p-0
    "
    data-testid="forecast-cell-input"
    @keydown.esc="props.onClose()"
    @keydown.enter="applyAdjustment"
    @keydown.tab.prevent="handleTab"
  />
  <AdjustForecastValueModal
    :anchor-el="props.anchorEl"
    :adjustment-percentage="adjustmentPercentage"
    :disable-percentages="props.displayValue === 0"
    :on-apply-percentage="handleApplyPercentage"
    :on-reset="props.onReset"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { evaluateExpression } from '../expression-evaluator'
import AdjustForecastValueModal from './adjust-forecast-value-modal.vue'

const props =
  defineProps<{
    anchorEl: HTMLElement
    displayValue: number
    adjustedDisplayValue: number
    savedAdjustmentPercentage: number | null
    savedExpression: string | null
    onApply: (
      adjustment: number,
      adjustmentPercentage: number | null,
      expression: string | null,
      fromQuickButton: boolean
    ) => void
    onTab: (
      adjustment: number,
      adjustmentPercentage: number | null,
      expression: string | null,
      backward: boolean
    ) => void
    onTabWithoutChange: (backward: boolean) => void
    onReset: () => void
    onClose: () => void
  }>()

const inputRef = ref<HTMLInputElement | null>(null)
const adjustmentPercentage = ref<number | null>(props.savedAdjustmentPercentage)

const formattedValue = computed(
  () => props.savedExpression ?? props.adjustedDisplayValue.toFixed(2)
)

function hasInputChanged(): boolean {
  return inputRef.value!.value !== formattedValue.value
}

function applyAdjustment(): void {
  if (!hasInputChanged()) {
    props.onClose()
    return
  }

  const expression = inputRef.value!.value
  const result = evaluateExpression(expression)

  if (result === null) {
    alert(window.I18n.t('reports.forecast.adjust_value.invalid_expression'))
  } else {
    props.onApply(
      result,
      computeAdjustmentPercentage(result),
      expression,
      false
    )
  }
}

function computeAdjustmentPercentage(adjustment: number): number | null {
  if (props.displayValue === 0) return null
  return (adjustment / props.displayValue - 1) * 100
}

function handleClickOutside(event: MouseEvent): void {
  if (!props.anchorEl.contains(event.target as Node)) {
    applyAdjustment()
  }
}

onMounted(() => {
  inputRef.value?.focus()
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

function handleTab(event: KeyboardEvent): void {
  if (!hasInputChanged()) {
    props.onTabWithoutChange(event.shiftKey)
    return
  }

  const expression = inputRef.value!.value
  const result = evaluateExpression(expression)

  if (result === null) {
    alert(window.I18n.t('reports.forecast.adjust_value.invalid_expression'))
  } else {
    props.onTab(
      result,
      computeAdjustmentPercentage(result),
      expression,
      event.shiftKey
    )
  }
}

function handleApplyPercentage(pct: number): void {
  adjustmentPercentage.value = pct
  const sign = pct >= 0 ? '+' : '-'
  const expression = `${props.displayValue.toFixed(2)}${sign}${Math.abs(pct)}%`
  props.onApply(props.displayValue * (1 + pct / 100), pct, expression, true)
}
</script>
